import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";

function isSpaceReservedByFocusedControl(): boolean {
    const target = document.activeElement;
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    if (target.isContentEditable) {
        return true;
    }
    return target.closest("button, a, [role='button']") !== null;
}

interface UseViewTableKeyboardShortcutsProps<
    TData extends { id: number | string },
> {
    data: TData[];
    focusedRowIndex: number;
    onFocusNext: () => void;
    onFocusPrevious: () => void;
    onSelectFocused: () => void;
    onDeselectAll: () => void;
    onToggleBulkSelectFocused: () => void;
    onToggleBulkSelectAll: () => void;
    onShiftSelectNext: () => void;
    onShiftSelectPrevious: () => void;
    onJumpToTop: () => void;
    onJumpToBottom: () => void;
    onPreviewFocused?: () => void;
    enabled?: boolean;
    bulkActionsEnabled?: boolean;
    isEnterSelectionDisabled?: boolean;
}

export function useViewTableKeyboardShortcuts<
    TData extends { id: number | string },
>({
    onFocusNext,
    onFocusPrevious,
    onSelectFocused,
    onDeselectAll,
    onToggleBulkSelectFocused,
    onToggleBulkSelectAll,
    onShiftSelectNext,
    onShiftSelectPrevious,
    onJumpToTop,
    onJumpToBottom,
    onPreviewFocused,
    enabled = false,
    bulkActionsEnabled = true,
    isEnterSelectionDisabled = false,
}: UseViewTableKeyboardShortcutsProps<TData>) {
    // Use the existing keyboard commands hook
    useKeyboardCommand({
        keys: "Enter",
        callback: onToggleBulkSelectFocused,
        options: {
            description: "Select or Unselect row",
            enabled: enabled && bulkActionsEnabled && !isEnterSelectionDisabled,
        },
    });
    useKeyboardCommand({
        keys: "x",
        callback: onToggleBulkSelectFocused,
        options: {
            description: "Select or Unselect row",
            enabled: enabled && bulkActionsEnabled,
        },
    });
    useKeyboardCommand({
        keys: "ArrowRight",
        callback: onSelectFocused,
        options: {
            description: "Show row detail",
            enabled,
        },
    });
    useKeyboardCommand({
        keys: "ArrowLeft",
        callback: onDeselectAll,
        options: {
            description: "Close row detail",
            enabled,
        },
    });
    useKeyboardCommand({
        keys: "Escape",
        callback: onDeselectAll,
        options: {
            description: "Close row detail",
            enabled,
        },
    });
    useKeyboardCommand({
        keys: "shift+a",
        callback: onToggleBulkSelectAll,
        options: {
            description: "Select or Unselect all rows",
            enabled: enabled && bulkActionsEnabled,
        },
    });
    useKeyboardCommand({
        keys: "ArrowDown",
        callback: onFocusNext,
        options: {
            description: "Go to next row",
            enabled,
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "ArrowUp",
        callback: onFocusPrevious,
        options: {
            description: "Go to previous row",
            enabled,
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "j",
        callback: onFocusNext,
        options: {
            description: "Go to next row",
            enabled,
        },
    });
    useKeyboardCommand({
        keys: "k",
        callback: onFocusPrevious,
        options: {
            description: "Go to previous row",
            enabled,
        },
    });
    useKeyboardCommand({
        keys: "shift+ArrowDown",
        callback: onShiftSelectNext,
        options: {
            description: "Select current row and go to the next row",
            enabled: enabled && bulkActionsEnabled,
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "shift+ArrowUp",
        callback: onShiftSelectPrevious,
        options: {
            description: "Select current row and go to the previous row",
            enabled: enabled && bulkActionsEnabled,
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "mod+ArrowDown",
        callback: onJumpToBottom,
        options: {
            description: "Jump to the last row",
            enabled,
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "space",
        callback: (keyboardEvent) => {
            if (isSpaceReservedByFocusedControl()) {
                return;
            }
            keyboardEvent.preventDefault();
            onPreviewFocused?.();
        },
        options: {
            description: "Preview attachment",
            enabled: enabled && onPreviewFocused !== undefined,
            preventDefault: false,
        },
    });
    useKeyboardCommand({
        keys: "mod+ArrowUp",
        callback: onJumpToTop,
        options: {
            description: "Jump to the first row",
            enabled,
            preventDefault: true,
        },
    });
}
